// src/components/Recomendacoes/RecomendacoesOverview.js

import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  CircularProgress,
  Chip,
  Alert
} from '@mui/material';
import {
  School as SchoolIcon,
  Recommend as RecommendIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import RecomendacaoCard from './RecomendacaoCard';

const RecomendacoesOverview = ({
  recomendacoes = [],
  perfil = null,
  minimal = false,
  isLoading = false,
  onPreInscricao
}) => {
  const navigate = useNavigate();

  const recomendacoesArray = Array.isArray(recomendacoes) ? recomendacoes : [];

  const HIGH_THRESHOLD = 70;
  const MEDIUM_THRESHOLD = 50;

  const highMatches = recomendacoesArray.filter(rec => Number(rec.pontuacaoTotal) >= HIGH_THRESHOLD).length;
  const mediumMatches = recomendacoesArray.filter(
    rec => Number(rec.pontuacaoTotal) >= MEDIUM_THRESHOLD && Number(rec.pontuacaoTotal) < HIGH_THRESHOLD
  ).length;
  const lowMatches = recomendacoesArray.filter(rec => Number(rec.pontuacaoTotal) < MEDIUM_THRESHOLD).length;

  const compatibilidadeMedia = recomendacoesArray.length > 0
    ? (
        recomendacoesArray.reduce((acc, rec) => acc + Number(rec.pontuacaoTotal || 0), 0) / recomendacoesArray.length
      ).toFixed(1)
    : 0;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (recomendacoesArray.length === 0) {
    return (
      <Alert severity="info">
        Nenhuma recomendação disponível no momento.
      </Alert>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Grid container spacing={4}>
        {/* Card de Matches */}
        <Grid item xs={12} md={4}>
          <Card sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            transition: 'transform 0.2s',
            '&:hover': {
              transform: 'translateY(-4px)'
            }
          }}>
            <CardContent>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 3,
                pb: 2,
                borderBottom: '1px solid #eee'
              }}>
                <RecommendIcon sx={{ 
                  mr: 1, 
                  color: '#2196f3',
                  fontSize: '2rem'
                }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Matches
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body1" sx={{ 
                  mb: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span>Alta Compatibilidade:</span>
                  <Chip 
                    label={highMatches}
                    color="success"
                    sx={{ fontWeight: 'bold' }}
                  />
                </Typography>
                <Typography variant="body1" sx={{ 
                  mb: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span>Média Compatibilidade:</span>
                  <Chip 
                    label={mediumMatches}
                    color="primary"
                    sx={{ fontWeight: 'bold' }}
                  />
                </Typography>
                <Typography variant="body1" sx={{ 
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span>Baixa Compatibilidade:</span>
                  <Chip 
                    label={lowMatches}
                    color="warning"
                    sx={{ fontWeight: 'bold' }}
                  />
                </Typography>
              </Box>
              {minimal && (
                <Button
                  onClick={() => navigate('/recomendacoes')}
                  variant="contained"
                  sx={{ 
                    mt: 'auto',
                    background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                    boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
                    borderRadius: '8px'
                  }}
                  fullWidth
                >
                  Clique Aqui para Ver Todas as Recomendações que temos Para Si
                </Button>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Card de Perfil */}
        <Grid item xs={12} md={4}>
          <Card sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            transition: 'transform 0.2s',
            '&:hover': {
              transform: 'translateY(-4px)'
            }
          }}>
            <CardContent>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 3,
                pb: 2,
                borderBottom: '1px solid #eee'
              }}>
                <SchoolIcon sx={{ 
                  mr: 1, 
                  color: '#4caf50',
                  fontSize: '2rem'
                }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Perfil
                </Typography>
              </Box>
              {perfil ? (
                <>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                      Tipo de Ensino:
                    </Typography>
                    <Chip
                      label={perfil.tipoEnsinoMedio}
                      color="primary"
                      variant="outlined"
                      sx={{ width: '100%', py: 1 }}
                    />
                  </Box>
                  <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Áreas de Destaque:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {perfil.areasDestaque?.map((area, index) => (
                      <Chip
                        key={index}
                        label={area}
                        color="success"
                        variant="outlined"
                        sx={{ borderRadius: '8px' }}
                      />
                    ))}
                  </Box>
                </>
              ) : (
                <Button
                  onClick={() => navigate('/recomendacoes/perfil')}
                  variant="contained"
                  sx={{ 
                    mt: 2,
                    background: 'linear-gradient(45deg, #4CAF50 30%, #81C784 90%)',
                    boxShadow: '0 3px 5px 2px rgba(76, 175, 80, .3)',
                    borderRadius: '8px'
                  }}
                  fullWidth
                >
                  Completar Perfil
                </Button>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Card de Métricas */}
        <Grid item xs={12} md={4}>
          <Card sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            transition: 'transform 0.2s',
            '&:hover': {
              transform: 'translateY(-4px)'
            }
          }}>
            <CardContent>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 3,
                pb: 2,
                borderBottom: '1px solid #eee'
              }}>
                <AssessmentIcon sx={{ 
                  mr: 1, 
                  color: '#ff9800',
                  fontSize: '2rem'
                }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Métricas
                </Typography>
              </Box>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Compatibilidade Média:
                </Typography>
                <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CircularProgress
                    variant="determinate"
                    value={parseFloat(compatibilidadeMedia)}
                    size={120}
                    thickness={4}
                    sx={{
                      color: compatibilidadeMedia >= 70 ? '#4caf50' :
                             compatibilidadeMedia >= 40 ? '#2196f3' :
                             '#ff9800'
                    }}
                  />
                  <Typography
                    variant="h4"
                    component="div"
                    sx={{
                      position: 'absolute',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold'
                    }}
                  >
                    {compatibilidadeMedia}%
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: '#f5f5f5',
                padding: 2,
                borderRadius: '8px'
              }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  Total de Cursos:
                </Typography>
                <Chip
                  label={recomendacoesArray.length}
                  color="primary"
                  sx={{ fontWeight: 'bold' }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Listar as Recomendações */}
      {!minimal && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom>
            Cursos Recomendados
          </Typography>
          <Grid container spacing={2}>
            {recomendacoesArray.map((rec, index) => (
              <Grid item xs={12} md={6} key={index}>
                <RecomendacaoCard
                  recomendacao={rec}
                  onPreInscricao={onPreInscricao}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  );
};

RecomendacoesOverview.propTypes = {
  recomendacoes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      cursoId: PropTypes.number,
      estudanteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      pontuacaoAcademica: PropTypes.number,
      pontuacaoGeografica: PropTypes.number,
      pontuacaoTotal: PropTypes.number,
      justificativa: PropTypes.string,
      nivel: PropTypes.string,
      curso: PropTypes.shape({
        id: PropTypes.number,
        nome: PropTypes.string,
        instituicao: PropTypes.shape({
          nome: PropTypes.string,
        }),
      }),
    })
  ).isRequired,
  perfil: PropTypes.shape({
    tipoEnsinoMedio: PropTypes.string,
    areasDestaque: PropTypes.arrayOf(PropTypes.string),
  }),
  minimal: PropTypes.bool,
  isLoading: PropTypes.bool,
  onPreInscricao: PropTypes.func,
};

export default RecomendacoesOverview;
